import { createFileRoute, Link } from '@tanstack/react-router'
import { useModal } from '@/components/modals/modal-container'
import { useQuery } from '@tanstack/react-query'

import { apiClient } from '@/lib/api/client'
import type { ContactEventBreakdown, OrganizationContactEvents } from '@/lib/api/types'
import { useAuth } from '@/lib/auth'

export const Route = createFileRoute('/_authenticated/contacts_/$userId')({
  validateSearch: (search: Record<string, unknown>): { org: number } => ({
    org: Number(search.org) || 0,
  }),
  component: ContactProfilePage,
})

function money(value: number | undefined): string {
  return `$${(value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Spend/refund rows are stored as negative ledger amounts; under labels
 *  like "Spent" and "Card refunds" the sign is noise — show magnitude. */
function magnitude(value: number | undefined): number {
  return Math.abs(value ?? 0)
}

function num(value: number | undefined): string {
  return (value ?? 0).toLocaleString()
}

/** Pre-tax/pre-fee "Loaded": exact charge-time base when the API provides
 *  it; until that deploys, derived by subtracting tax+fees from the full
 *  charge (close, but org-paid fee components can skew it slightly). */
function loadedBase(
  base: number | undefined,
  total: number | undefined,
  tax: number | undefined,
  fee: number | undefined,
): number {
  if (base != null) return base
  return Math.max(0, (total ?? 0) - (tax ?? 0) - (fee ?? 0))
}

/**
 * Full contact profile within one organization — the in-depth counterpart of
 * the Contacts drawer preview. Same org-scoped endpoint; unit caution as in
 * the types: loaded/refund figures are DOLLARS, spend/voucher/transfer/
 * balance figures are TOKENS.
 */
function ContactProfilePage() {
  const { userId } = Route.useParams()
  const { org } = Route.useSearch()
  const { token } = useAuth()

  const { data, isLoading, error } = useQuery<OrganizationContactEvents>({
    queryKey: ['org-contact-events', org, Number(userId)],
    queryFn: () => apiClient.getOrganizationUserEvents(token ?? '', org, Number(userId)),
    enabled: !!token && !!org,
  })

  const totals = data?.totals

  return (
    <div className="p-6 max-w-[1100px] mx-auto">
      <Link
        to="/contacts"
        className="text-sm text-neutral-500 hover:text-blue-600 dark:text-neutral-400 dark:hover:text-blue-400"
      >
        ← Contacts
      </Link>

      {!org ? (
        <p className="mt-6 text-sm text-amber-700 dark:text-amber-400">
          Missing organization context — open this profile from the Contacts list.
        </p>
      ) : isLoading ? (
        <p className="mt-6 text-sm text-neutral-500 dark:text-neutral-400">Loading…</p>
      ) : error ? (
        <p className="mt-6 text-sm text-amber-700 dark:text-amber-400">{(error as Error).message}</p>
      ) : data ? (
        <>
          <div className="mt-4 mb-6">
            <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">
              {data.user_name?.trim() || '—'}
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {[data.user_email, data.user_phone].filter(Boolean).join(' · ') || '—'}
              {' · '}{data.organization_name}
            </p>
          </div>

          {/* Full org-wide picture */}
          <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-5 mb-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-4">
              <Stat
                label="Loaded (pre-tax)"
                value={money(loadedBase(totals?.total_base, totals?.total_add_credit, totals?.total_tax, totals?.total_fee))}
              />
              <Stat label="Tax" value={money(totals?.total_tax)} />
              <Stat label="Fees" value={money(totals?.total_fee)} />
              <Stat label="Total charged" value={money(totals?.total_add_credit)} />
              <Stat label="Card refunds" value={money(magnitude(totals?.total_credit_refund))} />
              <Stat label="Orders" value={num(totals?.total_orders)} />
              <Stat label="Items" value={num(totals?.items_ordered)} />
              <Stat label="Spent" value={`${num(magnitude(totals?.total_spend_credit))} tokens`} />
              <Stat label="Vouchers" value={`${num(totals?.total_voucher_credit)} tokens`} />
              {/* transfers are legitimately signed (received − sent) */}
              <Stat label="Transfers" value={`${num(totals?.total_transfers)} tokens`} />
              <Stat label="Order refunds" value={`${num(magnitude(totals?.total_order_refund))} tokens`} />
              <Stat
                label="Balance"
                value={totals?.total_balance_value != null
                  ? money(totals.total_balance_value)
                  : `${num(totals?.total_balance)} tokens`}
              />
              <Stat label="Events attended" value={num(data.events.length)} />
            </div>
          </div>

          <h2 className="text-sm font-semibold text-neutral-900 dark:text-white mb-3">
            Event history
          </h2>
          <div className="space-y-4">
            {data.events.map((ev) => <ProfileEvent key={ev.event_id} ev={ev} userId={Number(userId)} />)}
            {data.events.length === 0 && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                No event history in this organization.
              </p>
            )}
          </div>
        </>
      ) : null}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-neutral-400 dark:text-neutral-500">{label}</p>
      <p className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-white">{value}</p>
    </div>
  )
}

function ProfileEvent({ ev, userId }: { ev: ContactEventBreakdown; userId: number }) {
  const { openUserModal } = useModal()
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-5">
      <div className="flex items-center gap-3 mb-4">
        {ev.event_logo ? (
          <img src={ev.event_logo} alt="" className="w-9 rounded object-cover shrink-0" />
        ) : (
          <span className="h-9 w-9 rounded bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-xs font-semibold text-neutral-500 dark:text-neutral-300 shrink-0">
            {ev.event_name.charAt(0)}
          </span>
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-neutral-900 dark:text-white truncate">{ev.event_name}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Attended {ev.attended_date ? ev.attended_date.slice(0, 10) : '—'}
            {ev.last_transaction ? ` · last activity ${ev.last_transaction.slice(0, 10)}` : ''}
          </p>
        </div>
        <button
          onClick={() => openUserModal(userId, ev.event_id, 'transactions')}
          className="ml-auto shrink-0 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          View Transactions
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-3">
        <Stat
          label="Loaded (pre-tax)"
          value={money(loadedBase(ev.total_base, ev.total_add_credit, ev.total_tax, ev.total_fee))}
        />
        <Stat label="Tax + fees" value={money((ev.total_tax ?? 0) + (ev.total_fee ?? 0))} />
        <Stat label="Total charged" value={money(ev.total_add_credit)} />
        <Stat label="Card refunds" value={money(magnitude(ev.total_credit_refund))} />
        <Stat label="Orders" value={num(ev.total_orders)} />
        <Stat label="Items" value={num(ev.items_ordered)} />
        <Stat label="Spent" value={`${num(magnitude(ev.total_spend_credit))} tokens`} />
        <Stat label="Vouchers" value={`${num(ev.total_voucher_credit)} tokens`} />
        <Stat label="Transfers" value={`${num(ev.total_transfers)} tokens`} />
        <Stat label="Order refunds" value={`${num(magnitude(ev.total_order_refund))} tokens`} />
        <Stat
          label="Balance"
          value={ev.balance_value != null ? money(ev.balance_value) : `${num(ev.balance)} tokens`}
        />
      </div>
    </div>
  )
}
